/**
 * Calcul pur (sans DOM ni dépendance de framework) du statut d'ouverture et
 * du dépliement « Voir horaires », à partir de la semaine reconnue par le
 * lecteur PHP des anciens horaires texte (`nielsen_parse_legacy_opening_hours()`).
 *
 * Fichier volontairement séparé de `StoreOpeningStatus.js` (qui gère le rendu
 * DOM et le composant de la fiche magasin) pour rester testable directement
 * en Node, sans navigateur ni build du thème : les tests Playwright du
 * chantier store-locator-revendeurs (point 3, cas H8-H11 et H17-H19)
 * importent ce module tel quel, avec une semaine fixe fournie par le test
 * (invariant R14 — jamais une fiche magasin réelle).
 *
 * Invariant R2 (voir docs/chantiers/store-locator-revendeurs.md, annexe) : le
 * serveur ne fournit que la semaine reconnue (données stables, cachables) ;
 * tout le calcul (heure courante, statut, « ferme bientôt ») a lieu ici, en
 * heure Europe/Paris, quel que soit le fuseau du visiteur.
 */

export const DAY_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

export const DAY_LABELS = {
  lundi: 'Lundi',
  mardi: 'Mardi',
  mercredi: 'Mercredi',
  jeudi: 'Jeudi',
  vendredi: 'Vendredi',
  samedi: 'Samedi',
  dimanche: 'Dimanche',
};

/** Une journée compte 1440 minutes : borne pour normaliser un éventuel dépassement. */
const MINUTES_PER_DAY = 24 * 60;

/**
 * Heure courante à Paris, quel que soit le fuseau du visiteur ou de la
 * machine qui exécute ce code (H17).
 * @param  {Date} date
 * @returns {{ daySlug: string, minutes: number }}
 */
export function getParisNow(date = new Date()) {
  const formatter = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value ?? '';

  const weekday = get('weekday').toLowerCase();
  const daySlug = DAY_ORDER.includes(weekday) ? weekday : DAY_ORDER[(date.getUTCDay() + 6) % 7];
  const hour = parseInt(get('hour'), 10) || 0;
  const minute = parseInt(get('minute'), 10) || 0;

  return { daySlug, minutes: hour * 60 + minute };
}

/** Formate des minutes depuis minuit en « HHhMM » (toujours deux chiffres de minutes, ex. 10h00, 12h30). */
export function formatMinutesAsHours(totalMinutes) {
  const normalized = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${hours}h${String(minutes).padStart(2, '0')}`;
}

/**
 * Cherche la prochaine ouverture à partir du jour/minute courants (inclus le
 * reste de la journée), en balayant jusqu'à 7 jours. Une semaine reconnue a
 * toujours au moins un jour ouvert (invariant du lecteur PHP), donc une
 * ouverture est toujours trouvée.
 * @param  {object} week
 * @param  {string} daySlug
 * @param  {number} minutes
 * @returns {{ daySlug: string, start: number, sameDay: boolean }|null}
 */
function findNextOpening(week, daySlug, minutes) {
  const todayIndex = DAY_ORDER.indexOf(daySlug);
  if (todayIndex === -1) {
    return null;
  }

  const todayRanges = week[daySlug] || [];
  const nextToday = todayRanges
    .filter(([start]) => start > minutes)
    .sort((a, b) => a[0] - b[0])[0];
  if (nextToday) {
    return { daySlug, start: nextToday[0], sameDay: true };
  }

  for (let offset = 1; offset <= 7; offset += 1) {
    const candidateSlug = DAY_ORDER[(todayIndex + offset) % 7];
    const ranges = week[candidateSlug] || [];
    if (ranges.length > 0) {
      const firstStart = ranges.reduce((min, [start]) => Math.min(min, start), ranges[0][0]);
      return { daySlug: candidateSlug, start: firstStart, sameDay: false };
    }
  }

  return null;
}

/**
 * Calcule le statut d'ouverture à l'instant `now`, en heure de Paris.
 * @param  {object|null} week Semaine reconnue par `nielsen_parse_legacy_opening_hours()`, ou `null`.
 * @param  {Date} now
 * @returns {{ state: 'open'|'closing-soon'|'closed', label: string }|null} `null` si aucun statut (pas de semaine reconnue).
 */
export function computeStoreOpeningStatus(week, now = new Date()) {
  if (!week) {
    return null;
  }

  const { daySlug, minutes } = getParisNow(now);
  const todayRanges = week[daySlug] || [];

  const currentRange = todayRanges.find(([start, end]) => minutes >= start && minutes < end);
  if (currentRange) {
    const [, end] = currentRange;
    const remaining = end - minutes;
    if (remaining <= 60) {
      return { state: 'closing-soon', label: `Ferme bientôt · dans ${remaining} min` };
    }
    return { state: 'open', label: `Ouvert · ferme à ${formatMinutesAsHours(end)}` };
  }

  const next = findNextOpening(week, daySlug, minutes);
  if (!next) {
    return { state: 'closed', label: 'Fermé' };
  }
  if (next.sameDay) {
    return { state: 'closed', label: `Fermé · ouvre à ${formatMinutesAsHours(next.start)}` };
  }
  return {
    state: 'closed',
    label: `Fermé · ouvre ${next.daySlug} à ${formatMinutesAsHours(next.start)}`,
  };
}

/**
 * Construit les lignes du dépliement « Voir horaires » (sept jours, jour
 * courant marqué).
 * @param  {object} week
 * @param  {Date} now
 * @returns {Array<{ daySlug: string, label: string, isToday: boolean, hoursLabel: string }>}
 */
export function formatOpeningWeek(week, now = new Date()) {
  const { daySlug: todaySlug } = getParisNow(now);
  return DAY_ORDER.map((daySlug) => {
    const ranges = week[daySlug] || [];
    const hoursLabel =
      ranges.length > 0
        ? ranges.map(([start, end]) => `${formatMinutesAsHours(start)}-${formatMinutesAsHours(end)}`).join(', ')
        : 'Fermé';
    return {
      daySlug,
      label: DAY_LABELS[daySlug],
      isToday: daySlug === todaySlug,
      hoursLabel,
    };
  });
}
